import { allSlideTypes } from '@/constant/slideTypes'
import appConfig from '@/config'
import { isFeatureEnabled } from '../featureToggles.module'

const DEFAULT_CUSTOM_SLIDE_SETTING = Object.freeze({
  enableQuestionTitle: true,
  enableQuestionDescription: true,
  enableTimeLimit: true,
  enableStopSubmitssionSetting: true,
  enableHideResultSetting: true,
  enableQuestionImage: true,
  enableVoteCount: true,
  enableLabelOtherSetting: false,
  enableMultipleSubmission: false,
  enableFullScreen: false,
  enableIframePreload: false,
  enableGameLobby: false,
})

const _buildConfigForType = (type, rootGetters) => {
  const builtin = allSlideTypes.find((slideType) => slideType.type === type)
  if (builtin) return builtin

  const custom = rootGetters['embedApp/customSlideTypeByType'](type)
  if (!custom) return {}

  return {
    ...custom,
    plugin: true,
    setting: { ...DEFAULT_CUSTOM_SLIDE_SETTING, ...(custom.setting || {}) },
  }
}

const _getters = {
  canUseSlidePlugin: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'AHA-39386-aha-slide-plugin') ||
      isFeatureEnabled(rootGetters, 'AHA-42884-new-slide-market-place')
  },

  slideActiveConfig: (_, __, rootState, rootGetters) => {
    const { type } = rootState.slideActive
    return _buildConfigForType(type, rootGetters)
  },

  // Resolve the slide-type config for an arbitrary slide type (not just the
  // active one). Used by the "Apply all" settings actions to decide which
  // slides in the presentation expose the same setting.
  slideConfigByType: (_, __, ___, rootGetters) => (type) => _buildConfigForType(type, rootGetters),

  isSlideActiveFullCanvasPlugin: (_, getters, ___, rootGetters) => {
    if (!isFeatureEnabled(rootGetters, 'AHA-39386-aha-slide-plugin')) return false
    const config = getters.slideActiveConfig
    // `enableFullScreen` is the canonical manifest key (see
    // aha-slide-plugin/packages/toolkit/schema/plugin-manifest.schema.json) —
    // the host's "fullCanvas" layout is the response to that opt-in.
    return Boolean(config.plugin && config.setting?.enableFullScreen)
  },

  // Reactive lookup of the slide that follows the active one in playback order.
  // Returns null when the active slide is the last one (or there is no active slide).
  // Skip-slide is intentionally ignored here — preload is a best-effort optimisation
  // and the skip-slide check is async; mis-preloading a skipped slide is wasted work
  // but never wrong.
  nextSlideAfterActive: (_, __, rootState) => {
    const slides = rootState.presentation?.slides
    const activeId = rootState.slideActive?.id
    if (!slides?.length || !activeId) return null
    const idx = slides.findIndex((s) => s.id === activeId)
    if (idx < 0 || idx >= slides.length - 1) return null
    return slides[idx + 1]
  },

  // URL the host can preload (plain hidden iframe) to warm browser caches for the
  // next slide's plugin canvas. Opt-in is the single switch: returns null unless
  // the next slide is a plugin slide type whose `setting.enableIframePreload` is
  // true (default false). No feature-flag gate — the per-plugin flag is enough,
  // and built-in plugin slide types (e.g. content-v2) ship under their own flags,
  // not `AHA-39386-aha-slide-plugin`.
  //
  // URL is built the same way SlidePluginIframeEmbed builds the real iframe src,
  // so the warmed URL matches the request made on navigation:
  //   - manifest plugins that ship an explicit `canvasUrl` → `${canvasUrl}/${id}`
  //   - built-in plugin types with no `canvasUrl` (content-v2, survey) →
  //     `${slidePluginUrl}/${type}/canvas/${id}`
  nextSlideIframePreloadUrl: (_, getters, __, rootGetters) => {
    const next = getters.nextSlideAfterActive
    if (!next?.type) return null
    const config = _buildConfigForType(next.type, rootGetters)
    if (!config.plugin || !config.setting?.enableIframePreload) return null
    return config.canvasUrl
      ? `${config.canvasUrl}/${next.id}`
      : `${appConfig.slidePluginUrl}/${next.type}/canvas/${next.id}`
  },
}

export default _getters
